/**
 * Smart Recommendations (IA)
 *
 * Intelligent recommendations based on user's trading, staking, and card data
 */

import { useMemo } from 'react';
import { Icons } from '../Icons';
import type { TradingBot, StakingPosition, CROCard } from '../../lib/api/cryptocom-api';
import { fmt } from '../../utils/formatters';

interface SmartRecommendationsProps {
  bots: TradingBot[];
  positions: StakingPosition[];
  currentCard?: CROCard | null;
  croPrice?: number;
}

interface Recommendation {
  id: string;
  type: 'upgrade' | 'optimization' | 'alert' | 'opportunity' | 'warning';
  priority: 'high' | 'medium' | 'low';
  title: string;
  description: string;
  action?: string;
  impact: string;
  icon: any;
  color: string;
}

const CARD_TIERS = [
  { tier: 'MIDNIGHT_BLUE', stake: 0, cashback: 1 },
  { tier: 'RUBY_STEEL', stake: 400, cashback: 2 },
  { tier: 'ROYAL_INDIGO', stake: 4000, cashback: 3 },
  { tier: 'ICY_WHITE', stake: 40000, cashback: 5 },
  { tier: 'OBSIDIAN', stake: 400000, cashback: 8 }
];

export function SmartRecommendations({ bots, positions, currentCard, croPrice = 0.10 }: SmartRecommendationsProps) {
  const recommendations = useMemo((): Recommendation[] => {
    const recs: Recommendation[] = [];

    // Calculate metrics
    const totalTradingProfit = bots.reduce((sum, b) => sum + b.profit, 0);
    const totalStakingRewards = positions.reduce((sum, p) => sum + p.totalRewards, 0);
    const totalStakedValue = positions.reduce((sum, p) => sum + p.amount, 0);
    const activeBots = bots.filter(b => b.status === 'ACTIVE').length;
    const avgBotProfit = activeBots > 0 ? totalTradingProfit / activeBots : 0;

    // 1. Card Tier Upgrade Recommendations
    if (currentCard) {
      const currentTierIndex = CARD_TIERS.findIndex(t => t.tier === currentCard.tier);
      const nextTier = currentTierIndex < CARD_TIERS.length - 1 ? CARD_TIERS[currentTierIndex + 1] : null;

      if (nextTier) {
        const croNeeded = nextTier.stake - currentCard.stakedAmount;
        const progress = ((currentCard.stakedAmount - CARD_TIERS[currentTierIndex].stake) /
                         (nextTier.stake - CARD_TIERS[currentTierIndex].stake)) * 100;

        // High priority if >75% progress
        if (progress >= 75) {
          recs.push({
            id: 'tier-upgrade-close',
            type: 'upgrade',
            priority: 'high',
            title: `Vous êtes à ${(100 - progress).toFixed(0)}% du tier ${nextTier.tier.replace('_', ' ')}!`,
            description: `Stakez seulement ${fmt.number(croNeeded)} CRO supplémentaires (≈${fmt.currency(croNeeded * croPrice)}) pour débloquer ${nextTier.cashback}% de cashback.`,
            action: `Staker ${fmt.number(croNeeded)} CRO`,
            impact: `+${nextTier.cashback - currentCard.cashbackRate}% cashback, nouveaux avantages premium`,
            icon: Icons.TrendingUp,
            color: '#10b981'
          });
        } else if (progress >= 50) {
          recs.push({
            id: 'tier-upgrade-halfway',
            type: 'upgrade',
            priority: 'medium',
            title: `Mi-chemin vers ${nextTier.tier.replace('_', ' ')}`,
            description: `Vous avez atteint ${progress.toFixed(0)}% du prochain tier. Continuez à accumuler du CRO!`,
            action: `Staker ${fmt.number(croNeeded)} CRO de plus`,
            impact: `Débloquer ${nextTier.cashback}% cashback`,
            icon: Icons.Award,
            color: '#3b82f6'
          });
        }

        // ROI analysis for upgrade
        const monthlySpending = 2000; // Estimate
        const currentMonthlyCashback = (monthlySpending * currentCard.cashbackRate) / 100;
        const nextMonthlyCashback = (monthlySpending * nextTier.cashback) / 100;
        const additionalCashback = nextMonthlyCashback - currentMonthlyCashback;
        const paybackMonths = (croNeeded * croPrice) / additionalCashback;

        if (paybackMonths <= 12) {
          recs.push({
            id: 'tier-upgrade-roi',
            type: 'opportunity',
            priority: 'high',
            title: `ROI Excellent sur upgrade ${nextTier.tier.replace('_', ' ')}`,
            description: `Avec ${fmt.currency(monthlySpending)}/mois de dépenses, récupérez votre investissement en ${paybackMonths.toFixed(1)} mois!`,
            action: 'Calculer mon ROI personnalisé',
            impact: `+${fmt.currency(additionalCashback * 12)}/an de cashback`,
            icon: Icons.DollarSign,
            color: '#10b981'
          });
        }
      }

      // Unlock date alert
      if (currentCard.daysRemaining <= 30 && currentCard.daysRemaining > 0) {
        recs.push({
          id: 'unlock-soon',
          type: 'alert',
          priority: currentCard.daysRemaining <= 7 ? 'high' : 'medium',
          title: `Unlock dans ${currentCard.daysRemaining} jours`,
          description: `Votre stake CRO sera disponible bientôt. Décidez si vous restez pour maintenir vos avantages carte.`,
          action: 'Renouveler le stake',
          impact: currentCard.daysRemaining <= 7 ? 'Action requise sous 1 semaine' : 'Planifier renouvellement',
          icon: Icons.Clock,
          color: currentCard.daysRemaining <= 7 ? '#f59e0b' : '#6b7280'
        });
      }
    }

    // 2. Portfolio Balance Recommendations
    const tradingStakingRatio = totalStakingRewards > 0
      ? totalTradingProfit / totalStakingRewards
      : totalTradingProfit > 0 ? Infinity : 0;

    if (tradingStakingRatio > 5) {
      recs.push({
        id: 'balance-more-staking',
        type: 'optimization',
        priority: 'medium',
        title: 'Portfolio trop concentré sur Trading',
        description: `Vos profits trading (${fmt.currency(totalTradingProfit)}) dominent vs staking (${fmt.currency(totalStakingRewards)}). Diversifiez avec du staking passif.`,
        action: 'Explorer le staking CRO',
        impact: 'Revenus passifs stables jusqu\'à 12% APY',
        icon: Icons.PieChart,
        color: '#8b5cf6'
      });
    } else if (tradingStakingRatio < 0.2 && totalTradingProfit > 0) {
      recs.push({
        id: 'balance-more-trading',
        type: 'optimization',
        priority: 'medium',
        title: 'Opportunité d\'augmenter les profits actifs',
        description: `Votre staking génère ${fmt.currency(totalStakingRewards)} mais seulement ${fmt.currency(totalTradingProfit)} en trading. Considérez plus de bots.`,
        action: 'Lancer un bot Grid ou DCA',
        impact: 'Potentiel de profits actifs plus élevés',
        icon: Icons.Bot,
        color: '#3b82f6'
      });
    }

    // 3. Bot Performance Analysis
    if (bots.length > 0) {
      const underperformingBots = bots.filter(b => b.profit < 0);
      const topPerformers = bots.filter(b => b.profitPercentage >= 10);

      if (underperformingBots.length > 0) {
        const totalLoss = underperformingBots.reduce((sum, b) => sum + Math.abs(b.profit), 0);
        recs.push({
          id: 'bot-underperforming',
          type: 'warning',
          priority: 'high',
          title: `${underperformingBots.length} bot(s) en perte`,
          description: `Perte totale de ${fmt.currency(totalLoss)}. Analysez les paramètres ou arrêtez ces bots.`,
          action: 'Réviser les bots en perte',
          impact: `Réduire les pertes de ${fmt.currency(totalLoss)}`,
          icon: Icons.AlertTriangle,
          color: '#ef4444'
        });
      }

      if (topPerformers.length > 0) {
        const topBot = topPerformers.sort((a, b) => b.profitPercentage - a.profitPercentage)[0];
        recs.push({
          id: 'bot-top-performer',
          type: 'opportunity',
          priority: 'medium',
          title: `Bot ${topBot.symbol} performe à +${topBot.profitPercentage.toFixed(1)}%!`,
          description: `Excellent résultat avec ${fmt.currency(topBot.profit)} de profit. Considérez augmenter l'allocation.`,
          action: `Dupliquer la stratégie ${topBot.type}`,
          impact: 'Capitaliser sur stratégie gagnante',
          icon: Icons.TrendingUp,
          color: '#10b981'
        });
      }

      // Low activity warning
      if (activeBots === 0 && bots.length > 0) {
        recs.push({
          id: 'bots-inactive',
          type: 'alert',
          priority: 'medium',
          title: 'Tous vos bots sont inactifs',
          description: `Vous avez ${bots.length} bot(s) configuré(s) mais aucun n'est actif. Opportunités manquées?`,
          action: 'Réactiver les bots',
          impact: 'Reprendre la génération de profits actifs',
          icon: Icons.Power,
          color: '#f59e0b'
        });
      }

      // Bot diversity
      const botTypes = new Set(bots.map(b => b.type));
      if (botTypes.size === 1 && bots.length >= 2) {
        const onlyType = Array.from(botTypes)[0];
        recs.push({
          id: 'bot-diversity',
          type: 'optimization',
          priority: 'low',
          title: 'Diversifiez vos stratégies de trading',
          description: `Tous vos bots sont de type ${onlyType}. Différentes stratégies performent dans différentes conditions.`,
          action: 'Explorer d\'autres types de bots',
          impact: 'Meilleure résilience de portfolio',
          icon: Icons.Grid,
          color: '#6b7280'
        });
      }
    } else {
      // No bots
      recs.push({
        id: 'no-bots',
        type: 'opportunity',
        priority: 'medium',
        title: 'Commencez le trading automatique',
        description: 'Vous n\'avez aucun bot actif. Les bots peuvent générer des profits 24/7 sans surveillance constante.',
        action: 'Créer votre premier bot',
        impact: 'Revenus actifs automatisés',
        icon: Icons.Bot,
        color: '#3b82f6'
      });
    }

    // 4. Staking Optimization
    if (positions.length > 0) {
      const avgAPY = positions.reduce((sum, p) => sum + p.apy, 0) / positions.length;
      const lowAPYPositions = positions.filter(p => p.apy < avgAPY - 2);

      if (lowAPYPositions.length > 0) {
        const lowAPYValue = lowAPYPositions.reduce((sum, p) => sum + p.amount, 0);
        recs.push({
          id: 'staking-low-apy',
          type: 'optimization',
          priority: 'medium',
          title: `${fmt.currency(lowAPYValue)} en staking sous-optimal`,
          description: `${lowAPYPositions.length} position(s) avec APY en dessous de la moyenne (${avgAPY.toFixed(1)}%). Considérez rebalancer.`,
          action: 'Optimiser allocation staking',
          impact: `Potentiel +${((avgAPY - lowAPYPositions[0].apy) * lowAPYValue / 100).toFixed(0)}$/an`,
          icon: Icons.RefreshCw,
          color: '#8b5cf6'
        });
      }

      // Concentrated risk
      const totalStaked = positions.reduce((sum, p) => sum + p.amount, 0);
      const largePositions = positions.filter(p => (p.amount / totalStaked) > 0.5);

      if (largePositions.length > 0) {
        recs.push({
          id: 'staking-concentration',
          type: 'warning',
          priority: 'low',
          title: 'Concentration élevée sur 1 produit',
          description: `Plus de 50% de votre staking est sur ${largePositions[0].type}. Diversifiez pour réduire le risque.`,
          action: 'Diversifier le staking',
          impact: 'Réduction du risque de concentration',
          icon: Icons.AlertCircle,
          color: '#f59e0b'
        });
      }
    } else if (totalTradingProfit > 500) {
      // Has trading profit but no staking
      recs.push({
        id: 'no-staking',
        type: 'opportunity',
        priority: 'high',
        title: 'Générez des revenus passifs avec le staking',
        description: `Avec ${fmt.currency(totalTradingProfit)} de profits trading, stakez une partie pour des revenus passifs jusqu'à 12% APY.`,
        action: 'Commencer le staking CRO',
        impact: 'Revenus passifs stables + benefits carte',
        icon: Icons.Lock,
        color: '#10b981'
      });
    }

    // 5. Market Timing (simplified)
    const recentBots = bots.filter(b => Date.now() - b.createdAt < 7 * 24 * 60 * 60 * 1000);
    if (recentBots.length === 0 && bots.length < 3 && totalTradingProfit > 100) {
      recs.push({
        id: 'market-timing',
        type: 'opportunity',
        priority: 'low',
        title: 'Opportunité: Lancez un nouveau bot',
        description: 'Aucun bot récent. Les marchés volatiles sont idéaux pour Grid Trading et DCA.',
        action: 'Configurer un bot Grid',
        impact: 'Profiter de la volatilité actuelle',
        icon: Icons.Zap,
        color: '#f59e0b'
      });
    }

    // Sort by priority
    const priorityOrder = { high: 0, medium: 1, low: 2 };
    return recs.sort((a, b) => priorityOrder[a.priority] - priorityOrder[b.priority]);
  }, [bots, positions, currentCard, croPrice]);

  const highPriorityCount = recommendations.filter(r => r.priority === 'high').length;

  return (
    <div className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <h2 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Icons.Sparkles size={24} />
          Recommandations Intelligentes
        </h2>
        {highPriorityCount > 0 && (
          <span style={{
            padding: '4px 12px',
            background: '#ef4444',
            color: 'white',
            borderRadius: '12px',
            fontSize: '12px',
            fontWeight: 600
          }}>
            {highPriorityCount} priorité haute
          </span>
        )}
      </div>

      {recommendations.length === 0 ? (
        <div style={{
          padding: '32px',
          textAlign: 'center',
          color: 'var(--muted)'
        }}>
          <Icons.CheckCircle size={48} style={{ margin: '0 auto 16px', opacity: 0.5 }} />
          <p style={{ fontSize: '16px', fontWeight: 600, marginBottom: '8px' }}>
            Excellent! Tout est optimisé
          </p>
          <p style={{ fontSize: '14px' }}>
            Votre portfolio est bien équilibré. Continuez comme ça!
          </p>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: '16px' }}>
          {recommendations.map(rec => {
            const getBorderColor = () => {
              if (rec.priority === 'high') return '#ef4444';
              if (rec.priority === 'medium') return '#f59e0b';
              return 'var(--border)';
            };

            const getPriorityLabel = () => {
              if (rec.priority === 'high') return { label: 'PRIORITÉ HAUTE', color: '#ef4444' };
              if (rec.priority === 'medium') return { label: 'Priorité Moyenne', color: '#f59e0b' };
              return { label: 'Info', color: '#6b7280' };
            };

            const priority = getPriorityLabel();

            return (
              <div
                key={rec.id}
                style={{
                  padding: '16px',
                  background: 'var(--bg)',
                  borderRadius: '8px',
                  borderLeft: `4px solid ${getBorderColor()}`,
                  transition: 'transform 0.2s',
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'translateX(4px)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'translateX(0)'}
              >
                <div style={{ display: 'flex', gap: '16px' }}>
                  {/* Icon */}
                  <div style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '8px',
                    background: `${rec.color}20`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    <rec.icon size={24} style={{ color: rec.color }} />
                  </div>

                  {/* Content */}
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                      <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600 }}>
                        {rec.title}
                      </h3>
                      <span style={{
                        padding: '2px 8px',
                        background: `${priority.color}20`,
                        color: priority.color,
                        borderRadius: '4px',
                        fontSize: '10px',
                        fontWeight: 600,
                        textTransform: 'uppercase'
                      }}>
                        {priority.label}
                      </span>
                    </div>

                    <p style={{
                      margin: '0 0 12px 0',
                      fontSize: '14px',
                      color: 'var(--muted)',
                      lineHeight: '1.5'
                    }}>
                      {rec.description}
                    </p>

                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      gap: '12px'
                    }}>
                      <div>
                        <div style={{ fontSize: '12px', color: '#888', marginBottom: '2px' }}>
                          Impact
                        </div>
                        <div style={{ fontSize: '13px', fontWeight: 500, color: rec.color }}>
                          {rec.impact}
                        </div>
                      </div>

                      {rec.action && (
                        <button style={{
                          padding: '8px 16px',
                          background: rec.color,
                          color: rec.color === '#f59e0b' || rec.color === '#6b7280' ? '#000' : '#fff',
                          border: 'none',
                          borderRadius: '6px',
                          fontSize: '13px',
                          fontWeight: 600,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px'
                        }}>
                          {rec.action}
                          <Icons.ArrowRight size={14} />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div style={{
        marginTop: '24px',
        padding: '12px',
        background: 'rgba(99, 102, 241, 0.1)',
        borderRadius: '6px',
        borderLeft: '3px solid #6366f1',
        fontSize: '12px',
        color: 'var(--muted)',
        lineHeight: '1.6'
      }}>
        <strong>🤖 IA:</strong> Ces recommandations sont générées automatiquement en analysant
        votre portfolio, performances historiques, et conditions de marché. Actualisées en temps réel.
      </div>
    </div>
  );
}
